/**
 * Date arithmetic and layout for the meetings calendar.
 *
 * Everything here works in the **browser's** wall clock, where the rest of
 * Pulse formats in Europe/London (see `time.ts`). That is deliberate and it is
 * the same call `time.ts` makes for `datetime-local` inputs: a calendar is a
 * grid of the viewer's own days and hours, and positioning a 09:00 block at
 * nine on the grid is the viewer's clock by definition. For a UK desk the two
 * clocks are the same clock; for a rep abroad the grid follows the phone, which
 * is what they would expect of their phone's calendar too.
 *
 * Weeks start on Monday, as a British diary does.
 */

export type CalendarView = "day" | "week" | "month";

export const MINUTE = 60_000;
export const DAY_MINUTES = 24 * 60;

export function startOfDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

/** Calendar days, not 24-hour steps -- a clock change must not shift the grid. */
export function addDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

export function addMonths(date: Date, months: number): Date {
  return new Date(date.getFullYear(), date.getMonth() + months, 1);
}

/** The Monday on or before `date`. */
export function startOfWeek(date: Date): Date {
  const day = startOfDay(date);
  const offset = (day.getDay() + 6) % 7;
  return addDays(day, -offset);
}

export function sameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

/** "2026-10-08", for the URL. Local parts, never `toISOString`. */
export function ymd(date: Date): string {
  const pad = (value: number) => String(value).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

/** The inverse of `ymd`, or today for anything that does not parse. */
export function parseYmd(value: string | null): Date {
  const match = value ? /^(\d{4})-(\d{2})-(\d{2})$/.exec(value) : null;
  if (!match) return startOfDay(new Date());
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

/** Minutes since local midnight. */
export function minuteOfDay(date: Date): number {
  return date.getHours() * 60 + date.getMinutes();
}

/**
 * The days a view shows and the instants to ask the server for.
 *
 * Month is always six whole weeks from the Monday on or before the first, as
 * Google draws it, so the grid never changes height between months.
 */
export function rangeOf(view: CalendarView, anchor: Date): { days: Date[]; from: Date; to: Date } {
  const first =
    view === "day"
      ? startOfDay(anchor)
      : view === "week"
        ? startOfWeek(anchor)
        : startOfWeek(new Date(anchor.getFullYear(), anchor.getMonth(), 1));
  const count = view === "day" ? 1 : view === "week" ? 7 : 42;
  const days = Array.from({ length: count }, (_, index) => addDays(first, index));
  return { days, from: first, to: addDays(first, count) };
}

/**
 * The days a week or month grid draws: Monday to Friday, with the weekend put
 * back only when something is booked on it.
 *
 * The desk works weekdays, and on a phone two empty columns were two-sevenths
 * of the screen spent on nothing -- every weekday a narrow sliver so Saturday
 * and Sunday could be blank. But a calendar must never hide a meeting, so a
 * weekend with anything on it in the range keeps both its days: both rather
 * than the one, so the month's grid stays whole weeks and the columns line up.
 */
export function workingDays(
  days: Date[],
  meetings: { startAt: string }[],
): Date[] {
  const weekend = (day: Date): boolean => day.getDay() === 0 || day.getDay() === 6;
  const busy = meetings.some((meeting) => {
    const start = new Date(meeting.startAt);
    return weekend(start) && days.some((day) => sameDay(day, start));
  });
  return busy ? days : days.filter((day) => !weekend(day));
}

/** One step back or forward in the current view. */
export function step(view: CalendarView, anchor: Date, direction: 1 | -1): Date {
  if (view === "day") return addDays(anchor, direction);
  if (view === "week") return addDays(anchor, 7 * direction);
  return addMonths(anchor, direction);
}

/**
 * The title over the grid, as Google writes it: "Saturday 10 October 2026",
 * "6 – 12 Oct 2026", "October 2026". A week across two months names both.
 */
export function rangeTitle(view: CalendarView, anchor: Date, short = false): string {
  /* The phone's version: a narrow header has room for "Sat 10 Oct", not
     "Saturday 10 October 2026". */
  if (short && view !== "month") {
    if (view === "day") {
      return anchor.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
    }
    const start = startOfWeek(anchor);
    const end = addDays(start, 6);
    return `${start.getDate()}–${end.toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`;
  }
  if (short) return anchor.toLocaleDateString("en-GB", { month: "short", year: "numeric" });

  if (view === "day") {
    return anchor.toLocaleDateString("en-GB", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  }
  if (view === "month") {
    return anchor.toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  }

  const start = startOfWeek(anchor);
  const end = addDays(start, 6);
  const sameMonth = start.getMonth() === end.getMonth();
  const sameYear = start.getFullYear() === end.getFullYear();
  const left = start.toLocaleDateString("en-GB", {
    day: "numeric",
    ...(sameMonth ? {} : { month: "short" }),
    ...(sameYear ? {} : { year: "numeric" }),
  });
  const right = end.toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  return `${left} – ${right}`;
}

/**
 * A meeting with no end is drawn as an hour long.
 *
 * "Open" is a real answer on the booking form -- a visit as long as it takes --
 * but a block with no height cannot be seen or clicked, and an hour is what
 * Google assumes too.
 */
export const OPEN_ENDED_MINUTES = 60;

export function endOf(meeting: { startAt: string; endAt: string | null }): Date {
  const start = new Date(meeting.startAt);
  return meeting.endAt
    ? new Date(meeting.endAt)
    : new Date(start.getTime() + OPEN_ENDED_MINUTES * MINUTE);
}

/**
 * Whether a meeting belongs in the all-day strip rather than on the hours.
 *
 * Pulse books meetings at times, so this is mostly a home for what the calendar
 * importer brings in: an event that fills whole days, which drawn on the hourly
 * grid would be a block the height of the screen hiding everything under it.
 */
export function isAllDay(meeting: { startAt: string; endAt: string | null }): boolean {
  if (!meeting.endAt) return false;
  return new Date(meeting.endAt).getTime() - new Date(meeting.startAt).getTime() >= DAY_MINUTES * MINUTE;
}

export interface Placed<T> {
  item: T;
  /** Minutes from midnight, clamped to the day. */
  top: number;
  bottom: number;
  /** Which of `columns` side-by-side lanes this sits in. */
  column: number;
  columns: number;
}

/**
 * Lay out one day's meetings so overlapping ones sit side by side.
 *
 * Google's rule, simplified: meetings that overlap -- directly or through a
 * chain of others -- form a cluster, every meeting in a cluster takes the
 * first lane free at its start, and the cluster is divided into as many equal
 * lanes as it needed. A meeting overlapping nothing gets the whole width.
 */
export function layoutDay<T extends { startAt: string; endAt: string | null }>(
  items: T[],
  day: Date,
): Placed<T>[] {
  const dayStart = startOfDay(day).getTime();

  const spans = items
    .map((item) => {
      const top = Math.max(0, (new Date(item.startAt).getTime() - dayStart) / MINUTE);
      const bottom = Math.min(DAY_MINUTES, (endOf(item).getTime() - dayStart) / MINUTE);
      /* Fifteen minutes at the least, so a zero-length meeting is still a
         block somebody can see and press. */
      return { item, top, bottom: Math.max(bottom, top + 15) };
    })
    .sort((a, b) => a.top - b.top || b.bottom - a.bottom);

  const placed: Placed<T>[] = [];
  let cluster: Placed<T>[] = [];
  let lanes: number[] = [];
  let clusterEnd = -1;

  const close = (): void => {
    for (const entry of cluster) entry.columns = lanes.length;
    cluster = [];
    lanes = [];
  };

  for (const span of spans) {
    if (span.top >= clusterEnd) {
      close();
      clusterEnd = -1;
    }

    let lane = lanes.findIndex((end) => end <= span.top);
    if (lane === -1) {
      lane = lanes.length;
      lanes.push(span.bottom);
    } else {
      lanes[lane] = span.bottom;
    }

    const entry: Placed<T> = { ...span, column: lane, columns: 1 };
    cluster.push(entry);
    placed.push(entry);
    clusterEnd = Math.max(clusterEnd, span.bottom);
  }
  close();

  return placed;
}

/** Round minutes to the nearest quarter-hour, which is what a drag snaps to. */
export function snap(minutes: number, to = 15): number {
  return Math.round(minutes / to) * to;
}
